import { useMemo, useState } from "react";
import { Alert, Box, Grid2 as Grid, MenuItem, Stack, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  itemsApi, nearExpiryApi, purchaseOrdersApi, reorderApi, reportsApi,
  salesInvoicesApi, warehousesApi,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { daysAgoIso, endOfDayUtc, formatLongDate, startOfDayUtc, todayIso } from "@/shared/datetime";
import { BarChart, ChartPanel, DonutChart, HBarList, compactInr } from "./charts";
import type { DonutSlice, HBarRow } from "./charts";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const num = new Intl.NumberFormat("en-IN");

/** How many days of sales the trend panel covers. */
const SALES_WINDOW = 14;
/** How far ahead the expiry panel looks. Matches the widest tab on the near-expiry queue. */
const EXPIRY_WINDOW = 90;

/**
 * The trailing window as calendar days in the display zone — oldest first.
 *
 * These are the same days the report groups by: it buckets on `DocumentDate AT TIME ZONE` the
 * configured business zone, so an axis built from local dates lines up with the keys it returns.
 */
function trailingDays(count: number): string[] {
  return Array.from({ length: count }, (_, i) => daysAgoIso(count - 1 - i));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "19 Aug" from a bare yyyy-MM-dd, without dragging the value through a timezone. */
function shortDay(yyyyMmDd: string): string {
  const [, m, d] = yyyyMmDd.split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1]}`;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuth(s => s.user);
  const hasPermission = useAuth(s => s.hasPermission);

  const canItems = hasPermission("inventory.item.view");
  const canStock = hasPermission("inventory.stock.view");
  const canPo = hasPermission("procurement.po.view");
  const canSales = hasPermission("sales.invoice.view");
  const canSalesReport = hasPermission("reports.management.view");
  const canStockReport = hasPermission("reports.stock.view");

  const today = todayIso();
  const days = useMemo(() => trailingDays(SALES_WINDOW), []);

  // Stock-scoped cards read one pharmacy at a time: reorder points and expiry exposure are
  // per-warehouse figures, and summing them across sites answers no question anyone asks.
  const [warehouseId, setWarehouseId] = useState("");
  const warehouses = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
    enabled: canStock,
    staleTime: 5 * 60_000,
  });
  const warehouseList = warehouses.data?.items ?? [];
  const activeWarehouseId = warehouseId || warehouseList[0]?.id || "";

  const itemCount = useQuery({
    queryKey: ["dash-item-count"],
    queryFn: () => itemsApi.list({ isActive: true, page: 1, pageSize: 1 }),
    enabled: canItems,
    staleTime: 5 * 60_000,
  });

  // "Open" is everything raised but not yet received or killed. The list endpoint filters one status
  // at a time, so the two open states are counted with a pageSize-1 probe each.
  const openPos = useQuery({
    queryKey: ["dash-open-pos"],
    queryFn: async () => {
      const [submitted, approved] = await Promise.all([
        purchaseOrdersApi.list({ status: "Submitted", page: 1, pageSize: 1 }),
        purchaseOrdersApi.list({ status: "Approved", page: 1, pageSize: 1 }),
      ]);
      return { submitted: submitted.totalCount, approved: approved.totalCount };
    },
    enabled: canPo,
    staleTime: 60_000,
  });

  const todaysInvoices = useQuery({
    queryKey: ["dash-today-invoices", today],
    queryFn: () => salesInvoicesApi.list({
      fromDate: startOfDayUtc(today), toDate: endOfDayUtc(today), page: 1, pageSize: 1,
    }),
    enabled: canSales,
    staleTime: 30_000,
  });

  const dailySales = useQuery({
    queryKey: ["dash-daily-sales", days[0], days[days.length - 1]],
    queryFn: () => reportsApi.dailySales(days[0], days[days.length - 1]),
    enabled: canSalesReport,
    staleTime: 60_000,
  });

  const reorder = useQuery({
    queryKey: ["dash-reorder", activeWarehouseId],
    queryFn: () => reorderApi.suggestions(activeWarehouseId),
    enabled: canStock && !!activeWarehouseId,
    staleTime: 60_000,
  });

  const nearExpiry = useQuery({
    queryKey: ["dash-near-expiry", activeWarehouseId],
    queryFn: () => nearExpiryApi.list(EXPIRY_WINDOW, activeWarehouseId || undefined),
    enabled: canStock && !!activeWarehouseId,
    staleTime: 60_000,
  });

  const poStatus = useQuery({
    queryKey: ["dash-po-status"],
    queryFn: () => reportsApi.poStatus(),
    enabled: canStockReport,
    staleTime: 60_000,
  });

  // ---- derived ------------------------------------------------------------

  const salesByDay = useMemo(() => {
    const map = new Map((dailySales.data ?? []).map(r => [r.day.slice(0, 10), r]));
    return days.map(d => {
      const row = map.get(d);
      return {
        label: shortDay(d),
        value: row?.netAmount ?? 0,
        tooltip: `${shortDay(d)} — ${inr.format(row?.netAmount ?? 0)} over ${row?.billCount ?? 0} bill(s)`,
      };
    });
  }, [dailySales.data, days]);

  const salesTotal = salesByDay.reduce((s, d) => s + d.value, 0);

  const expirySlices = useMemo<DonutSlice[]>(() => {
    const rows = nearExpiry.data ?? [];
    const bucket = (test: (d: number) => boolean) =>
      rows.filter(r => test(r.daysToExpiry)).reduce((s, r) => s + r.estimatedExposure, 0);
    return [
      { label: "Already expired", value: bucket(d => d < 0), color: "#B42318" },
      { label: "≤ 30 days", value: bucket(d => d >= 0 && d <= 30), color: "#E4694A" },
      { label: "31 – 60 days", value: bucket(d => d > 30 && d <= 60), color: "#D9A441" },
      { label: "61 – 90 days", value: bucket(d => d > 60 && d <= 90), color: "#7FA6C9" },
    ];
  }, [nearExpiry.data]);

  const expiryExposure = expirySlices.reduce((s, x) => s + x.value, 0);

  const reorderRows = useMemo<HBarRow[]>(() => {
    const rows = [...(reorder.data ?? [])];
    rows.sort((a, b) => {
      if (a.isOutOfStock !== b.isOutOfStock) return a.isOutOfStock ? -1 : 1;
      return (b.reorderPoint - b.inventoryPositionQty) - (a.reorderPoint - a.inventoryPositionQty);
    });
    return rows.slice(0, 8).map(r => ({
      label: `${r.name}${r.strength ? ` ${r.strength}` : ""}`,
      value: r.suggestedQty,
      color: r.isOutOfStock ? "#B42318" : "#9A6410",
      hint: `${r.isOutOfStock ? "Out of stock" : `${num.format(r.availableQty)} on hand`}` +
            ` · reorder point ${num.format(r.reorderPoint)}` +
            `${r.onOrderQty > 0 ? ` · ${num.format(r.onOrderQty)} on order` : ""}`,
    }));
  }, [reorder.data]);

  const outOfStockCount = (reorder.data ?? []).filter(r => r.isOutOfStock).length;

  const pipelineRows = useMemo<HBarRow[]>(() => {
    const rows = poStatus.data ?? [];
    const sum = (pick: (r: (typeof rows)[number]) => number) => rows.reduce((s, r) => s + pick(r), 0);
    return [
      { label: "Draft", value: sum(r => r.draft), color: "#94A3B8" },
      { label: "Submitted", value: sum(r => r.submitted), color: "#7FA6C9" },
      { label: "Approved", value: sum(r => r.approved), color: "#1F5FA8" },
      { label: "Received / posted", value: sum(r => r.posted), color: "#17875A" },
      { label: "Cancelled", value: sum(r => r.cancelled), color: "#B42318" },
    ];
  }, [poStatus.data]);

  const nothingVisible = !canItems && !canStock && !canPo && !canSales;

  return (
    <Box>
      <PageHeader
        title={`Welcome back, ${user?.fullName?.split(" ")[0] ?? "User"}`}
        subtitle={`${user?.roleName ?? ""} · ${formatLongDate(new Date())}`}
        actions={canStock && warehouseList.length > 0 ? (
          <TextField
            select size="small" label="Pharmacy / warehouse" sx={{ minWidth: 240 }}
            value={activeWarehouseId}
            onChange={e => setWarehouseId(e.target.value)}
          >
            {warehouseList.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
        ) : undefined}
      />

      {nothingVisible && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your role has no catalog, inventory, procurement or billing permissions, so there are no
          figures to show here. Use the sidebar for the modules you do have access to.
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {canItems && (
          <StatCard
            label="Items in catalog"
            value={itemCount.isLoading ? "…" : itemCount.isError ? "—" : num.format(itemCount.data?.totalCount ?? 0)}
            hint={itemCount.isError ? "Couldn't load" : "Active SKUs"}
            onClick={() => navigate("/catalog/items")}
          />
        )}
        {canPo && (
          <StatCard
            label="Open POs"
            value={openPos.isLoading ? "…" : openPos.isError ? "—"
              : num.format((openPos.data?.submitted ?? 0) + (openPos.data?.approved ?? 0))}
            severity="info"
            hint={openPos.isError ? "Couldn't load"
              : `${openPos.data?.submitted ?? 0} submitted · ${openPos.data?.approved ?? 0} approved`}
            onClick={() => navigate("/procurement/purchase-orders")}
          />
        )}
        {canSales && (
          <StatCard
            label="Today's invoices"
            value={todaysInvoices.isLoading ? "…" : todaysInvoices.isError ? "—"
              : num.format(todaysInvoices.data?.totalCount ?? 0)}
            severity="success"
            hint={todaysInvoices.isError ? "Couldn't load" : "Bills raised today"}
            onClick={() => navigate("/sales/invoices")}
          />
        )}
        {canStock && (
          <StatCard
            label="Low-stock alerts"
            value={!activeWarehouseId ? "—" : reorder.isLoading ? "…" : reorder.isError ? "—"
              : num.format(reorder.data?.length ?? 0)}
            severity="warning"
            hint={!activeWarehouseId ? "No warehouse" : reorder.isError ? "Couldn't load"
              : `${outOfStockCount} out of stock`}
            onClick={() => navigate("/procurement/reorder-queue")}
          />
        )}
        {canStock && (
          <StatCard
            label="Expiry exposure"
            value={!activeWarehouseId ? "—" : nearExpiry.isLoading ? "…" : nearExpiry.isError ? "—"
              : compactInr(expiryExposure)}
            severity="critical"
            hint={!activeWarehouseId ? "No warehouse" : nearExpiry.isError ? "Couldn't load"
              : `${num.format(nearExpiry.data?.length ?? 0)} batches within ${EXPIRY_WINDOW} days`}
            onClick={() => navigate("/inventory/near-expiry")}
          />
        )}
      </Stack>

      <Grid container spacing={2}>
        {canSalesReport && (
          <Grid size={{ xs: 12, lg: 8 }}>
            <ChartPanel
              title={`Sales — last ${SALES_WINDOW} days`}
              subtitle={dailySales.data?.length
                ? `${inr.format(salesTotal)} net across the window`
                : "Net of tax, posted bills only"}
              loading={dailySales.isLoading}
              error={dailySales.isError ? errorMessage(dailySales.error) : null}
              isEmpty={(dailySales.data?.length ?? 0) === 0}
              emptyText={`No posted bills in the last ${SALES_WINDOW} days. A bill counts here once it is posted — drafts and submitted bills are not sales yet.`}
            >
              <BarChart data={salesByDay} formatValue={compactInr} labelEvery={2} />
            </ChartPanel>
          </Grid>
        )}

        {canStock && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <ChartPanel
              title={`Expiry risk — next ${EXPIRY_WINDOW} days`}
              subtitle="Stock value by remaining shelf life"
              loading={nearExpiry.isLoading}
              error={nearExpiry.isError ? errorMessage(nearExpiry.error) : null}
              isEmpty={!activeWarehouseId || expiryExposure === 0}
              emptyText={activeWarehouseId
                ? `Nothing expiring within ${EXPIRY_WINDOW} days at this warehouse.`
                : "Pick a warehouse to see expiry exposure."}
            >
              <DonutChart slices={expirySlices} formatValue={compactInr} centerLabel="at risk" />
            </ChartPanel>
          </Grid>
        )}

        {canStock && (
          <Grid size={{ xs: 12, md: 6 }}>
            <ChartPanel
              title="Buy next — below reorder point"
              subtitle="Suggested order quantity, most urgent first"
              loading={reorder.isLoading}
              error={reorder.isError ? errorMessage(reorder.error) : null}
              isEmpty={!activeWarehouseId || reorderRows.length === 0}
              emptyText={activeWarehouseId
                ? "Every item is at or above its reorder point."
                : "Pick a warehouse to see reorder suggestions."}
            >
              <HBarList rows={reorderRows} formatValue={n => `${num.format(n)} units`} />
            </ChartPanel>
          </Grid>
        )}

        {canStockReport && (
          <Grid size={{ xs: 12, md: 6 }}>
            <ChartPanel
              title="Purchase pipeline"
              subtitle="Purchase orders by status, all vendors"
              loading={poStatus.isLoading}
              error={poStatus.isError ? errorMessage(poStatus.error) : null}
              isEmpty={pipelineRows.every(r => r.value === 0)}
              emptyText="No purchase orders raised yet."
            >
              <HBarList rows={pipelineRows} formatValue={n => num.format(n)} />
            </ChartPanel>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
